import { useEffect, useId, useRef } from 'react';
import { AlertCircle, Info, Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { useDialogs } from '../../../components/ui/DialogProvider';
import { Switch } from '../../../components/ui/Switch';
import { ADDED_MARK, REMOVED_MARK } from '../../composer/ThreadPostChanges';
import { REVISIT_COPY } from './revisitCopy';
import { useRevisit, type RevisitContextValue } from './RevisitProvider';

const BOX = 'flex items-start gap-3 rounded-xl border px-4 py-3 text-sm';
const NEUTRAL = 'border-line bg-card text-ink';
const FAILED =
  'border-red-200 bg-red-50 text-red-700 dark:border-error-dark/30 dark:bg-error-dark-tint dark:text-error-dark';
/** The composer's AI result banner (ThreadConvertBanner), so both Show changes read alike. */
const UPDATED =
  'rounded-xl border border-success/20 bg-success-tint/70 px-4 py-3 dark:border-success-dark/30 dark:bg-success-dark-tint/70';

function CloseButton({ onClick, disabled = false }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      aria-label={REVISIT_COPY.close}
      title={REVISIT_COPY.close}
      disabled={disabled}
      onClick={onClick}
      className="focus-ring shrink-0 rounded-md p-1.5 text-ink-faint hover:bg-chip hover:text-ink-strong disabled:cursor-not-allowed disabled:opacity-30"
    >
      <X className="h-4 w-4" aria-hidden />
    </button>
  );
}

/**
 * Revisit Brand Setup's re-read banner, over the step pills (spec:131-139):
 * - running: "AI is re-reading your sources", while every field waits, read-only;
 * - done: "AI updated your brand details" with its sentence, Undo and restore original text, Regenerate,
 *   the Show changes switch (on by default) with its Added and Removed legend while it is on, and a close
 *   button that hides the banner (Show changes goes off with it);
 * - a re-read that found nothing to change says so;
 * - a failure, or a refused start on step 1, as an alert, with Try again where trying again can help.
 * A polite live region says when a re-read starts and ends. The title takes the focus when a new result
 * comes while focus is nowhere (the Regenerate that had it is gone) or inside the banner.
 * Renders nothing outside a revisit's re-read.
 *
 * `disabled`: the wizard is saving a step. Undo and Regenerate wait for it, or the step would land with the
 * proposal in it after they had read the saved values.
 */
export function RevisitBanner({ disabled = false }: { disabled?: boolean }) {
  const revisit = useRevisit();
  if (!revisit) return null;
  return <ActiveRevisitBanner revisit={revisit} wizardBusy={disabled} />;
}

function ActiveRevisitBanner({ revisit, wizardBusy }: { revisit: RevisitContextValue; wizardBusy: boolean }) {
  const { confirm } = useDialogs();
  const titleId = useId();
  const root = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLParagraphElement>(null);
  const { run, busy, actionError } = revisit;
  const review = run.kind === 'review' && !run.closed ? run : null;

  // Once per result, never off something that holds the focus outside the banner.
  const focusToken = review?.token ?? null;
  useEffect(() => {
    if (focusToken === null) return;
    const active = document.activeElement;
    const nowhere = active == null || active === document.body;
    if (nowhere || root.current?.contains(active)) title.current?.focus();
  }, [focusToken]);

  const actionsOff = busy !== null || wizardBusy;

  const onUndo = async () => {
    if (!review) return;
    if (!revisit.undoLoses() || (await confirm(REVISIT_COPY.undoConfirm))) await revisit.undo(review.token);
  };
  const onRegenerate = async () => {
    if (!review) return;
    if (!revisit.edited() || (await confirm(REVISIT_COPY.regenerateConfirm))) await revisit.regenerate(review.token);
  };

  const announcement =
    run.kind === 'running'
      ? REVISIT_COPY.running
      : review
        ? review.changed > 0
          ? REVISIT_COPY.title
          : REVISIT_COPY.nothing
        : '';

  let banner = null;
  if (run.kind === 'idle' && revisit.startError) {
    banner = (
      <div role="alert" className={`${BOX} ${FAILED}`}>
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <p className="min-w-0 flex-1">{revisit.startError}</p>
        <CloseButton onClick={revisit.dismissStartError} />
      </div>
    );
  } else if (run.kind === 'running') {
    banner = (
      <div className={`${BOX} ${NEUTRAL}`}>
        <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-ink-muted motion-reduce:animate-none" aria-hidden />
        <p>{REVISIT_COPY.running}</p>
      </div>
    );
  } else if (run.kind === 'failed') {
    banner = (
      <div role="alert" className={`${BOX} ${FAILED}`}>
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1 space-y-2">
          <p>{run.message}</p>
          {actionError && <p>{actionError}</p>}
        </div>
        {run.retry && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="shrink-0"
            loading={busy === 'retry'}
            onClick={() => void revisit.retry()}
          >
            {REVISIT_COPY.tryAgain}
          </Button>
        )}
        <CloseButton onClick={revisit.close} disabled={busy !== null} />
      </div>
    );
  } else if (review && review.changed === 0) {
    banner = (
      <div ref={root} role="group" aria-labelledby={titleId} className={`${BOX} ${NEUTRAL}`}>
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
        <div className="min-w-0 flex-1 space-y-2">
          <p ref={title} id={titleId} tabIndex={-1}>
            {REVISIT_COPY.nothing}
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={actionsOff}
            loading={busy === 'regenerate'}
            onClick={() => void onRegenerate()}
          >
            {REVISIT_COPY.regenerate}
          </Button>
          {actionError && (
            <p role="alert" className="text-red-700 dark:text-error-dark">
              {actionError}
            </p>
          )}
        </div>
        <CloseButton onClick={revisit.close} disabled={busy !== null} />
      </div>
    );
  } else if (review) {
    banner = (
      <div ref={root} role="group" aria-labelledby={titleId} className={UPDATED}>
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-success dark:text-success-dark" aria-hidden />
          <div className="min-w-0 flex-1 text-sm">
            <p ref={title} id={titleId} tabIndex={-1} className="font-semibold text-ink-strong">
              {REVISIT_COPY.title}
            </p>
            <p className="mt-0.5 text-ink">{REVISIT_COPY.body}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={actionsOff}
                loading={busy === 'undo'}
                onClick={() => void onUndo()}
              >
                {REVISIT_COPY.undo}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={actionsOff}
                loading={busy === 'regenerate'}
                onClick={() => void onRegenerate()}
              >
                {REVISIT_COPY.regenerate}
              </Button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
              <Switch checked={review.showChanges} onChange={revisit.setShowChanges} label={REVISIT_COPY.showChanges} />
              {review.showChanges && (
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-muted">
                  {/* On white chips: the banner's own tint would swallow the green sample. */}
                  <span className="rounded-md border border-line bg-card px-1.5 py-0.5">
                    <span className={ADDED_MARK}>{REVISIT_COPY.legendAdded}</span>
                  </span>
                  <span className="rounded-md border border-line bg-card px-1.5 py-0.5">
                    <span className={REMOVED_MARK}>{REVISIT_COPY.legendRemoved}</span>
                  </span>
                  <span>{REVISIT_COPY.readOnlyNote}</span>
                </p>
              )}
            </div>
            {actionError && (
              <p role="alert" className="mt-2 text-red-700 dark:text-error-dark">
                {actionError}
              </p>
            )}
          </div>
          <CloseButton onClick={revisit.close} disabled={busy !== null} />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Always in the page, so what it says is announced (a region added with its text may not be). */}
      <div role="status" className="sr-only">
        {announcement}
      </div>
      {banner}
    </>
  );
}
